import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  CircularProgress,
  Card,
  CardContent
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import confetti from 'canvas-confetti'
import Context from '../models/Context'
import { FUBUser } from '../services/fubApi'
import {
  DotloopProfile,
  DotloopTemplate,
  TransactionType,
  LoopStatus,
  TRANSACTION_TYPE_LABELS,
  LoopCreationRequest
} from '../models/DotloopTypes'
import dotloopApi from '../services/dotloopApi'
import { saveLeadLoop } from '../services/loopsApi'
import PropertyDetailsSection from './PropertyDetailsSection'

interface LoopCreationFormProps {
  fubContext?: Context
  selectedAgent?: FUBUser | null
  selectedProfile: DotloopProfile | null
  selectedTemplate?: DotloopTemplate | null
  onApiRequest?: (request: any) => void
  onApiResponse?: (response: any) => void
  onApiError?: (error: string) => void
  onSuccess?: () => void
}

export default function LoopCreationForm({
  fubContext,
  selectedAgent,
  selectedProfile,
  selectedTemplate,
  onApiRequest,
  onApiResponse,
  onApiError,
  onSuccess
}: LoopCreationFormProps) {
  const navigate = useNavigate()
  // Basic Loop Info
  const [loopName, setLoopName] = useState('')
  const [transactionType, setTransactionType] = useState<TransactionType>('PURCHASE_OFFER')
  
  // Client Information
  const [clientName, setClientName] = useState('')
  const [clientEmail, setClientEmail] = useState('')
  const [clientPhone, setClientPhone] = useState('')
  
  // Property Details
  const [propertyDetails, setPropertyDetails] = useState({
    streetNumber: '',
    streetName: '',
    unitNumber: '',
    city: '',
    state: '',
    zipCode: '',
    county: ''
  })
  
  // UI state
  const [creating, setCreating] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [createdLoopUrl, setCreatedLoopUrl] = useState<string | null>(null)

  // Populate form with FUB data
  useEffect(() => {
    if (fubContext?.person) {
      const person = fubContext.person
      const firstName = person.firstName || ''
      const lastName = person.lastName || ''
      const fullName = `${firstName} ${lastName}`.trim()
      
      // Set client name
      setClientName(fullName)
      setLoopName(fullName ? `${fullName} - New Transaction` : 'New Transaction')

      // Extract email (first email or empty)
      if (person.emails && person.emails.length > 0) {
        setClientEmail(person.emails[0].value || '')
      }

      // Extract phone (first phone or empty)
      if (person.phones && person.phones.length > 0) {
        setClientPhone(person.phones[0].value || '')
      }

      // Extract address if available
      if (person.address) {
        // Try to parse the street address into number and name
        const street = person.address?.street || ''
        const streetMatch = street.match(/^(\d+)\s+(.+)$/)
        
        setPropertyDetails(prev => ({
          ...prev,
          streetNumber: streetMatch ? streetMatch[1] : '',
          streetName: streetMatch ? streetMatch[2] : street,
          unitNumber: '',
          city: person.address?.city || '',
          state: person.address?.state || '',
          zipCode: person.address?.zip || ''
        }))
      }
    }
  }, [fubContext])

  // Update loop name when transaction type changes
  useEffect(() => {
    if (clientName) {
      const typeLabel = TRANSACTION_TYPE_LABELS[transactionType]
      setLoopName(`${clientName} - ${typeLabel}`)
    }
  }, [transactionType, clientName])

  const handleCreateLoop = async () => {
    if (!selectedProfile) {
      setError('Please select a DotLoop profile')
      return
    }

    if (!loopName.trim()) {
      setError('Loop name is required')
      return
    }

    setCreating(true)
    setError(null)
    setSuccess(false)

    try {
      // Determine loop status based on transaction type
      const status: LoopStatus = transactionType.includes('PURCHASE') || transactionType.includes('LEASE_OFFER')
        ? 'PRE_OFFER'
        : 'PRE_LISTING'

      // Build loop creation request
      const loopData: LoopCreationRequest = {
        profile_id: selectedProfile.id,
        name: loopName.trim(),
        status: status,
        transaction_type: transactionType,
        ...(selectedTemplate?.id && { loop_template_id: selectedTemplate.id })
      }

      // Add participants if client info is provided
      if (clientName.trim() || clientEmail.trim()) {
        const participants = []
        
        // Add client as buyer or seller based on transaction type
        if (clientName.trim()) {
          const role = transactionType.includes('PURCHASE') || transactionType.includes('LEASE_OFFER')
            ? 'BUYER'
            : 'SELLER'
          
          participants.push({
            fullName: clientName.trim(),
            email: clientEmail.trim() || undefined,
            phone: clientPhone.trim() || undefined,
            role: role
          })
        }

        // Add agent as listing or buying agent
        if (selectedAgent) {
          const agentRole = transactionType.includes('LISTING')
            ? 'LISTING_AGENT'
            : 'BUYING_AGENT'
          
          participants.push({
            fullName: `${selectedAgent.firstName} ${selectedAgent.lastName}`.trim(),
            email: selectedAgent.email,
            role: agentRole
          })
        }

        if (participants.length > 0) {
          loopData.participants = participants as any
        }
      }

      // Add property if street address is provided
      if (propertyDetails.streetNumber.trim() || propertyDetails.streetName.trim()) {
        loopData.property = {
          streetNumber: propertyDetails.streetNumber.trim() || undefined,
          streetName: propertyDetails.streetName.trim() || undefined,
          unitNumber: propertyDetails.unitNumber.trim() || undefined,
          city: propertyDetails.city.trim() || undefined,
          state: propertyDetails.state.trim() || undefined,
          zipCode: propertyDetails.zipCode.trim() || undefined,
          county: propertyDetails.county.trim() || undefined
        } as any
      }

      // DEBUG: Log loop data being sent
      console.log('🔍 DEBUG - LoopCreationForm loopData:', {
        hasFubPerson: !!fubContext?.person,
        hasFubAddress: !!fubContext?.person?.address,
        propertyDetailsNonEmpty: {
          streetNumber: propertyDetails.streetNumber.trim().length > 0,
          streetName: propertyDetails.streetName.trim().length > 0,
          city: propertyDetails.city.trim().length > 0,
          state: propertyDetails.state.trim().length > 0,
          zipCode: propertyDetails.zipCode.trim().length > 0
        },
        loopDataHasProperty: !!loopData.property,
        loopDataPropertyKeys: loopData.property ? Object.keys(loopData.property).filter(k => (loopData.property as any)[k] !== undefined) : [],
        loopDataHasParticipants: !!(loopData.participants && loopData.participants.length),
        loopDataParticipantCount: loopData.participants ? loopData.participants.length : 0
      })

      // Notify parent of request (for debugging)
      if (onApiRequest) {
        onApiRequest(loopData)
      }

      console.log('Creating loop with data:', loopData)

      // Create the loop
      const response = await dotloopApi.createLoop(loopData)

      console.log('Loop created:', response)

      // Notify parent of response (for debugging)
      if (onApiResponse) {
        onApiResponse(response)
      }

      // Save loop to database
      if (fubContext?.person?.id && response.loop_id) {
        try {
          await saveLeadLoop(fubContext.person.id, {
            dotloop_loop_id: response.loop_id,
            dotloop_profile_id: selectedProfile.id,
            loop_name: loopName.trim(),
            loop_url: response.loop_url,
            transaction_type: transactionType
          })
          console.log('Loop saved to database')
        } catch (dbErr) {
          console.error('Failed to save loop to database:', dbErr)
          // Don't fail the whole operation if DB save fails
        }
      }

      setSuccess(true)
      setCreatedLoopUrl(response.loop_url)
      
      // Trigger confetti celebration!
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      })
      
      // Fire confetti from both sides for extra celebration
      setTimeout(() => {
        confetti({
          particleCount: 50,
          angle: 60,
          spread: 55,
          origin: { x: 0 }
        })
        confetti({
          particleCount: 50,
          angle: 120,
          spread: 55,
          origin: { x: 1 }
        })
      }, 250)
      
      // Navigate back to home page after a short delay
      setTimeout(() => {
        if (onSuccess) {
          onSuccess()
        }
        const currentSearch = window.location.search
        navigate(`/${currentSearch}`)
      }, 2500) // 2.5 seconds to enjoy the confetti

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create loop'
      setError(errorMessage)
      
      if (onApiError) {
        onApiError(errorMessage)
      }
      
      console.error('Error creating loop:', err)
    } finally {
      setCreating(false)
    }
  }

  const canCreate = selectedProfile && loopName.trim() && !creating

  if (success && createdLoopUrl) {
    return (
      <Card sx={{ mx: 1.5, mb: 1.5, backgroundColor: '#e8f5e9' }}>
        <CardContent>
          <Stack spacing={1.5} alignItems="center">
            <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main' }} />
            <Typography variant="h6" align="center" color="success.main">
              Loop Created Successfully!
            </Typography>
            <Typography variant="body2" align="center" color="text.secondary">
              Your DotLoop transaction has been created and is ready to use.
            </Typography>
            <Button
              variant="contained"
              color="success"
              endIcon={<OpenInNewIcon />}
              href={createdLoopUrl}
              target="_blank"
              rel="noopener noreferrer"
              fullWidth
            >
              Open Loop in DotLoop
            </Button>
            <Typography variant="caption" color="text.secondary">
              Returning to home page...
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    )
  }

  return (
    <Stack spacing={1.5} sx={{ px: 1.5 }}>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Create DotLoop Transaction
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Review and edit the information below before creating your loop
          </Typography>
        </CardContent>
      </Card>

      {/* Loop Details */}
      <Card>
        <CardContent>
          <Stack spacing={1.5}>
            <Typography variant="subtitle1" fontWeight="bold">
              Loop Details
            </Typography>

            <TextField
              fullWidth
              label="Loop Name"
              value={loopName}
              onChange={(e) => setLoopName(e.target.value)}
              required
              helperText="A descriptive name for this transaction"
            />

            <FormControl fullWidth required>
              <InputLabel>Transaction Type</InputLabel>
              <Select
                value={transactionType}
                label="Transaction Type"
                onChange={(e) => setTransactionType(e.target.value as TransactionType)}
              >
                {Object.entries(TRANSACTION_TYPE_LABELS).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </CardContent>
      </Card>

      {/* Client Information */}
      <Card>
        <CardContent>
          <Stack spacing={1.5}>
            <Typography variant="subtitle1" fontWeight="bold">
              Client Information
            </Typography>

            <TextField
              fullWidth
              label="Client Name"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              helperText="The buyer or seller for this transaction"
            />

            <TextField
              fullWidth
              label="Client Email"
              type="email"
              value={clientEmail}
              onChange={(e) => setClientEmail(e.target.value)}
            />

            <TextField
              fullWidth
              label="Client Phone"
              type="tel"
              value={clientPhone}
              onChange={(e) => setClientPhone(e.target.value)}
            />
          </Stack>
        </CardContent>
      </Card>

      {/* Property Information */}
      <PropertyDetailsSection
        propertyDetails={propertyDetails}
        onChange={(field, value) => 
          setPropertyDetails(prev => ({ ...prev, [field]: value }))
        }
      />

      {/* Agent Information */}
      {selectedAgent && (
        <Card>
          <CardContent>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Agent
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedAgent.firstName} {selectedAgent.lastName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedAgent.email}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Error Display */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Create Button */}
      <Button
        variant="contained"
        onClick={handleCreateLoop}
        disabled={!canCreate}
        fullWidth
        sx={{ mb: 1.5 }}
      >
        {creating ? (
          <>
            <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
            Creating Loop...
          </>
        ) : (
          'Create Loop in DotLoop'
        )}
      </Button>
    </Stack>
  )
}

